import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadCatalogueProductManagement} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  createZadCatalogueConfig,
  fetchZadCatalogue,
  fetchZadCatalogueById,
  fetchZadCatalogueByProduct,
  removeZadCatalogueImage,
  updateZadCatalogueConfig,
  updateZadCatalogueStatus,
  uploadZadCatalogueImage,
} from './service'
import {FetchZadCatalogueResponse, ZadCatalogueState} from './types'

const initialState: ZadCatalogueState = {
  items: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadCatalogueState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadCatalogueState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertItem(state: ZadCatalogueState, item?: ZadCatalogueProductManagement | null) {
  if (!item?._id) return
  const index = state.items.findIndex((row) => row._id === item._id)
  if (index >= 0) state.items[index] = item
  else state.items.unshift(item)
}

const zadCatalogueSlice = createSlice({
  name: 'zadCatalogue',
  initialState,
  reducers: {
    clearZadCatalogueError: (state: ZadCatalogueState) => {
      clearError(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadCatalogue.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadCatalogue.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadCatalogue.fulfilled,
      (state, action: PayloadAction<FetchZadCatalogueResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.items = action.payload.items
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    const actionPending = (state: ZadCatalogueState) => {
      state.actionLoading = true
      clearError(state)
    }
    const actionRejected = (state: ZadCatalogueState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    }
    const actionFulfilled = (state: ZadCatalogueState, action: PayloadAction<{item: ZadCatalogueProductManagement | null}>) => {
      state.actionLoading = false
      clearError(state)
      upsertItem(state, action.payload.item)
    }

    ;[
      fetchZadCatalogueById,
      fetchZadCatalogueByProduct,
      createZadCatalogueConfig,
      updateZadCatalogueConfig,
      updateZadCatalogueStatus,
      uploadZadCatalogueImage,
      removeZadCatalogueImage,
    ].forEach((thunk) => {
      builder.addCase(thunk.pending, actionPending)
      builder.addCase(thunk.rejected, actionRejected)
      builder.addCase(thunk.fulfilled, actionFulfilled)
    })
  },
})

export default zadCatalogueSlice.reducer
export const actions = zadCatalogueSlice.actions
export {initialState as zadCatalogueInitialState}
